import React, { useEffect, useRef } from 'react'
import classNames from 'classnames'
import PremiumBadge from '../PremiumBadge'
import ReactTooltip from 'react-tooltip'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
declare let M: any

interface Props {
  tabs: React.ReactNode // Tab components
  onShow: (id: string) => void
  tabRef: React.RefObject<HTMLUListElement | null>
  initialActiveId?: string
}

const TabHeader: React.FC<Props> = ({ tabs, onShow, tabRef, initialActiveId }) => {
  useEffect(() => {
    ReactTooltip.rebuild()
  }, [])

  // I did these changes because the client wanted the load tab to
  // link to the group pop up in network, specifically the overview tab.
  // It navigated to overview tab however the contents were not showing
  // and if we wanted to change the tab it was stuck on overview. So I
  // fixed these bugs.

  const tabsInstanceRef = useRef<any | null>(null)
  const didSelectInitialRef = useRef(false)

  const initialSelectTimeoutRef = useRef<number | null>(null)
  const updateIndicatorRafRef = useRef<number | null>(null)

  // To check if the tab exists.
  const elIsInDom = (): boolean => {
    const el = tabRef.current
    return el !== null && el !== undefined && document.contains(el)
  }

  const safeInit = (): void => {
    if (tabRef.current == null || tabsInstanceRef.current != null) return
    if (!elIsInDom()) return
    tabsInstanceRef.current = M.Tabs.init(tabRef.current, {
      swipeable: false,
      duration: 300,
      responsiveThreshold: Infinity
    })
  }

  const selectID = (id: string): void => {
    if (id === '') return
    if (!elIsInDom()) return
    if (tabsInstanceRef.current == null) safeInit()
    try {
      tabsInstanceRef.current?.select(id)
      onShow(id)
    } catch {
      /* ignore if element is gone during modal close */
    }
  }

  // This prevents the tab to get stuck on overview.
  const safeDestroy = (): void => {
    if (tabsInstanceRef.current == null) return
    const inst = tabsInstanceRef.current as { el?: Element, destroy?: () => void } | null
    const instEl = inst?.el
    if (instEl != null && document.contains(instEl)) {
      try {
        inst?.destroy?.()
      } catch {}
    }
    tabsInstanceRef.current = null
  }

  // To initialize the selected tab.
  useEffect(() => {
    safeInit()

    if (!didSelectInitialRef.current) {
      const candidate =
        initialActiveId ??
        (typeof window !== 'undefined' && window.location.hash !== ''
          ? window.location.hash.slice(1)
          : '')

      if (candidate !== '') {
        initialSelectTimeoutRef.current = window.setTimeout(() => {
          selectID(candidate)
          didSelectInitialRef.current = true
        }, 0)
      } else {
        didSelectInitialRef.current = true
      }
    }

    return () => {
      if (initialSelectTimeoutRef.current !== null) {
        clearTimeout(initialSelectTimeoutRef.current)
        initialSelectTimeoutRef.current = null
      }
      if (updateIndicatorRafRef.current !== null) {
        cancelAnimationFrame(updateIndicatorRafRef.current)
        updateIndicatorRafRef.current = null
      }
      safeDestroy()
    }
    // eslint-disable-next-line
  }, [tabRef])

  // To update the tab change.
  useEffect(() => {
    updateIndicatorRafRef.current = requestAnimationFrame(() => {
      if (!elIsInDom()) return
      try {
        tabsInstanceRef.current?.updateTabIndicator?.()
      } catch {}
    })
    return () => {
      if (updateIndicatorRafRef.current !== null) {
        cancelAnimationFrame(updateIndicatorRafRef.current)
        updateIndicatorRafRef.current = null
      }
    }
    // eslint-disable-next-line
  }, [tabs])

  return (
    <>
      {
        React.Children.toArray(tabs).map(tab => {
          if (typeof tab !== 'object' || tab === null || !('props' in tab)) return null

          const { id, title, disabled = false, tooltip, showPremiumBadge = false, cy } = tab.props as {
            id: string
            title: string
            disabled: boolean | undefined
            tooltip: string | undefined
            showPremiumBadge: boolean | undefined
            cy: string | undefined
          }
          return (
            <li
              data-cy={cy ?? `${id}-link`}
              key={`${id}-header`}
              onClick={e => {
                if (disabled) {
                  e.preventDefault()
                  return
                }
                onShow(id)
              }}
              className={classNames('tab col s3', { disabled })}
            >
              {showPremiumBadge && <PremiumBadge isSuperPremium={false} />}
              <a href={`#${id}`}>
                {title}
                {(tooltip != null && tooltip !== '') && (
                  <FontAwesomeIcon
                    icon='info-circle'
                    data-tip={tooltip}
                    data-for='higher-z-tooltip'
                    className='information-icon'
                  />
                )}
              </a>
            </li>
          )
        })
      }
    </>
  )
}

export default TabHeader
